import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Shield, TrendingUp, Target, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RiskProfileResults = ({ user, onLogout }) => {
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingOverride, setPendingOverride] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuestionnaire();
    fetchPendingOverride();
  }, []);

  const fetchPendingOverride = async () => {
    try {
      const { data } = await axios.get(`${API}/approvals/mine/pending`, { withCredentials: true });
      const override = (data || []).find(a => a.entity_type === 'risk_override');
      setPendingOverride(override || null);
    } catch (error) {
      // Non-critical - just skip the banner if this fails
      console.error('Error checking pending overrides:', error);
    }
  };

  const fetchQuestionnaire = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Add a small delay to ensure backend has saved the data
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const response = await axios.get(`${API}/questionnaire/me`, config);
      console.log('Fetched questionnaire:', response.data);
      
      // Check if questionnaire exists and has required data
      if (response.data && response.data.risk_score !== undefined) {
        setQuestionnaire(response.data);
      } else {
        console.log('No questionnaire found, redirecting to questionnaire page');
        navigate('/questionnaire');
      }
    } catch (error) {
      console.error('Error fetching questionnaire:', error);
      if (error.response?.status === 404) {
        // No questionnaire found
        navigate('/questionnaire');
      }
    } finally {
      setLoading(false);
    }
  };

  const getRiskProfileColor = (profile) => {
    switch (profile) {
      case 'Conservative':
        return { bg: 'bg-[#fffdf0]', border: 'border-ugmonk-border', text: 'text-ugmonk-dark', icon: 'text-ugmonk-dark' };
      case 'Balanced':
        return { bg: 'bg-[#fffdf0]', border: 'border-ugmonk-border', text: 'text-ugmonk-dark', icon: 'text-ugmonk-dark' };
      case 'Aggressive':
        return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'text-red-600' };
      default:
        return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', icon: 'text-gray-600' };
    }
  };

  const getRiskProfileDescription = (profile) => {
    const descriptions = {
      Conservative: {
        title: 'Conservative Investor',
        summary: 'You prioritize capital preservation and stable returns over growth. You prefer lower-risk investments with predictable outcomes.',
        characteristics: [
          'Focus on capital preservation',
          'Prefer stable, predictable returns',
          'Lower tolerance for market volatility',
          'Shorter investment horizon or approaching retirement',
          'Income generation is important'
        ],
        allocation: {
          'Equity': 10,
          'Fixed Income': 75,
          'International': 0,
          'Alternatives': 5,
          'Gold': 10
        }
      },
      Balanced: {
        title: 'Balanced Investor',
        summary: 'You seek a balance between growth and stability. You are comfortable with moderate market fluctuations for reasonable returns.',
        characteristics: [
          'Balance between growth and stability',
          'Moderate risk tolerance',
          'Comfortable with market fluctuations',
          'Medium to long-term investment horizon',
          'Seeking both income and capital appreciation'
        ],
        allocation: {
          'Equity': 35,
          'Fixed Income': 45,
          'International': 10,
          'Alternatives': 5,
          'Gold': 5
        }
      },
      Aggressive: {
        title: 'Aggressive Investor',
        summary: 'You prioritize long-term capital growth and are comfortable with significant market volatility. You have a high risk tolerance and long investment horizon.',
        characteristics: [
          'Focus on maximum capital appreciation',
          'High tolerance for volatility',
          'Comfortable with short-term losses',
          'Long-term investment horizon',
          'Can withstand market downturns'
        ],
        allocation: {
          'Equity': 60,
          'Fixed Income': 15,
          'International': 15,
          'Alternatives': 5,
          'Gold': 5
        }
      }
    };
    return descriptions[profile] || descriptions.Balanced;
  };

  const getAllocationChart = (allocation) => {
    const orangeShades = ['#FED879', '#CCD5AE', '#CEDEAB', '#DEE4DA', '#CFCECE'];
    
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c}%',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ffd87a',
        borderWidth: 1,
        textStyle: { color: '#1f2937' }
      },
      legend: {
        orient: 'vertical',
        right: '5%',
        top: 'center',
        textStyle: { color: '#6b7280', fontSize: 12 }
      },
      series: [
        {
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['35%', '50%'],
          avoidLabelOverlap: false,
          label: {
            show: true,
            position: 'inside',
            formatter: '{c}%',
            fontSize: 11,
            color: '#fff',
            fontWeight: 'bold'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold'
            }
          },
          data: [
            { value: allocation['Equity'], name: 'Equity', itemStyle: { color: orangeShades[0] } },
            { value: allocation['Fixed Income'], name: 'Fixed Income', itemStyle: { color: orangeShades[1] } },
            { value: allocation['International'], name: 'International', itemStyle: { color: orangeShades[2] } },
            { value: allocation['Alternatives'], name: 'Alternatives', itemStyle: { color: orangeShades[3] } },
            { value: allocation['Gold'], name: 'Gold', itemStyle: { color: orangeShades[4] } }
          ].filter(item => item.value > 0)
        }
      ]
    };
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ugmonk-yellow"></div>
        </div>
      </Layout>
    );
  }

  if (!questionnaire) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex flex-col items-center justify-center h-96">
          <AlertCircle size={64} className="text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Risk Profile Found</h2>
          <p className="text-gray-600 mb-6">Please complete the questionnaire first.</p>
          <button
            onClick={() => navigate('/questionnaire')}
            className="px-6 py-3 bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black rounded-lg font-medium"
          >
            Complete Questionnaire
          </button>
        </div>
      </Layout>
    );
  }

  const riskProfile = questionnaire.risk_profile_determined || 'Balanced';
  const riskScore = questionnaire.risk_score || 50;
  const colors = getRiskProfileColor(riskProfile);
  const profileInfo = getRiskProfileDescription(riskProfile);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#fffdf0] mb-4">
            <CheckCircle size={48} className="text-ugmonk-dark" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
            Your Risk Profile
          </h1>
          <p className="text-lg text-gray-600">
            Based on your comprehensive assessment
          </p>
        </div>

        {pendingOverride && (
          <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <AlertCircle size={20} className="text-yellow-700 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-yellow-900">
                A risk profile change is pending approval
              </p>
              <p className="text-sm text-yellow-800">
                Your RM requested a change to your risk profile on {new Date(pendingOverride.requested_at).toLocaleDateString()}.
                This will take effect once a checker approves it - the profile shown below is your current, active profile.
              </p>
            </div>
          </div>
        )}

        {/* Risk Profile Card */}
        <div className={`${colors.bg} border-2 ${colors.border} rounded-2xl p-8`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={`p-4 rounded-xl bg-white shadow-sm`}>
                <Shield size={32} className={colors.icon} />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900" style={{fontFamily: "'PerfectNineties', serif"}}>
                  {profileInfo.title}
                </h2>
                <p className="text-sm text-gray-600 mt-1">Risk Score: {riskScore}/100</p>
              </div>
            </div>
            <div className="text-right">
              <div className={`inline-flex items-center px-4 py-2 rounded-full ${colors.bg} border ${colors.border}`}>
                <span className={`text-sm font-semibold ${colors.text}`}>{riskProfile}</span>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed">
            {profileInfo.summary}
          </p>
        </div>

        {/* Score Visualization */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Tolerance Scale</h3>
          <div className="relative">
            <div className="flex justify-between mb-2">
              <span className="text-xs text-ugmonk-dark font-medium">Conservative</span>
              <span className="text-xs text-ugmonk-dark font-medium">Balanced</span>
              <span className="text-xs text-red-600 font-medium">Aggressive</span>
            </div>
            <div className="h-3 bg-gradient-to-r from-[#fffdf0] via-blue-200 to-red-200 rounded-full relative">
              <div 
                className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2"
                style={{ left: `${riskScore}%` }}
              >
                <div className="w-6 h-6 rounded-full bg-gray-900 border-4 border-white shadow-lg"></div>
              </div>
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-xs text-gray-500">0</span>
              <span className="text-xs text-gray-500">35</span>
              <span className="text-xs text-gray-500">65</span>
              <span className="text-xs text-gray-500">100</span>
            </div>
          </div>
        </div>

        {/* Key Characteristics */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target size={20} className="text-ugmonk-dark" />
            Key Characteristics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {profileInfo.characteristics.map((char, index) => (
              <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-[#fffdf0]/50">
                <CheckCircle size={18} className="text-ugmonk-dark mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">{char}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Allocation */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp size={20} className="text-ugmonk-dark" />
            Sanctum Wealth Recommended Allocation
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <ReactECharts 
                option={getAllocationChart(profileInfo.allocation)} 
                style={{ height: '280px' }}
                opts={{ renderer: 'svg' }}
              />
            </div>
            <div className="space-y-3">
              {Object.entries(profileInfo.allocation).filter(([_, value]) => value > 0).map(([asset, percentage]) => (
                <div key={asset} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                  <span className="text-sm font-medium text-gray-700">{asset}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-[#ffd87a] h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-bold text-gray-900 w-12 text-right">{percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 p-4 bg-[#fffdf0] border border-ugmonk-border rounded-lg">
            <p className="text-sm text-gray-700">
              <strong className="text-ugmonk-dark">Note:</strong> This allocation is based on Sanctum Wealth's house view for {riskProfile} investors. Your RM will work with you to create a personalized portfolio aligned with this framework.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center pb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 px-6 py-3 bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black rounded-lg font-medium shadow-sm transition-all"
          >
            Go to Dashboard
            <ArrowRight size={18} />
          </button>
          <button
            onClick={() => navigate('/questionnaire')}
            className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:border-[#ffd87a] hover:text-ugmonk-dark transition-all"
          >
            Retake Assessment
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default RiskProfileResults;
